import React from 'react';
import { Box, Typography, LinearProgress, Skeleton } from '@mui/material';
import PropTypes from 'prop-types';
import { PALETTE, alpha} from '../theme';

/**
 * Loading state for the Map Shaper result panel.
 *
 * Renders a shimmering skeleton that fills the result area (so the layout
 * doesn't jump when the real image lands) plus a context-aware status line.
 * The `label` prop lets the caller say WHAT is being processed — the shape
 * name during shaping, or "Uploading your design…" during a cup order —
 * which is more reassuring than a generic spinner.
 */
const ProcessingStatus = ({ progress = 0, label = 'Processing your image…' }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 1
            }}
        >
            <Skeleton
                variant="rounded"
                animation="wave"
                sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: { xs: 180, md: 260 },
                    maxHeight: 360,
                    borderRadius: 2,
                    bgcolor: alpha('primary', 0.08)
                }}
            />

            <Box sx={{ width: '100%', maxWidth: 320, textAlign: 'center' }}>
                <Typography
                    variant="body1"
                    sx={{ color: PALETTE.primary, fontWeight: 600, mb: 1 }}
                >
                    {label}
                </Typography>
                <LinearProgress
                    variant={progress > 0 ? 'determinate' : 'indeterminate'}
                    value={progress > 0 ? progress : undefined}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha('primary', 0.15),
                        '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(45deg, #e8632d, #b8401a)',
                            borderRadius: 4
                        }
                    }}
                />
                {progress > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: PALETTE.textMuted }}>
                        {progress}% complete
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

ProcessingStatus.propTypes = {
    progress: PropTypes.number,
    label: PropTypes.string
};

export default ProcessingStatus;
